import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, MapPin, Shirt } from "lucide-react";
import { PhotoGallery } from "@/components/PhotoGallery";
import { useApi } from "@/lib/api";
import { Loader, Empty, NewsRow, VideoCard } from "@/components/Blocks";
import { sportLabel, FLAG, fmtDate } from "@/lib/utils";
import { useT } from "@/lib/i18n";

const TABS = ["Overview", "News", "Stats", "Videos"];

export default function AthleteProfile() {
  const { slug } = useParams();
  const { t } = useT();
  const [tab, setTab] = useState("Overview");
  const { data: a, isLoading, error } = useApi("athlete", `/athletes/${slug}`);
  if (isLoading) return <Loader />;
  if (error || !a) return <div className="container-x py-20"><Empty text="Athlete not found." /></div>;
  const stats = a.stats || {};
  const age = stats.born ? Math.floor((Date.now() - new Date(stats.born)) / 31557600000) : null;

  return (
    <div data-testid="athlete-profile-page">
      <section className="relative grain bg-[#0B1120] text-white">
        {a.image_full && <img src={a.image_full} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 blur-sm" />}
        <div className="container-x relative grid gap-8 py-10 md:grid-cols-[280px_1fr] md:items-end">
          <div className="aspect-[4/5] w-56 overflow-hidden rounded-2xl border-4 border-white/10 bg-slate-800 shadow-2xl md:w-full">
            {a.image ? <img src={a.image} alt={a.name} className="h-full w-full object-cover object-top" /> : <div className="grid h-full place-items-center font-display text-7xl font-black text-white/20">{a.name[0]}</div>}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#F5B301]">{t(sportLabel(a.sport))}</p>
            <h1 className="mt-2 flex items-center gap-3 font-display text-5xl font-black sm:text-6xl" data-testid="athlete-name">{a.name} <img src={FLAG} alt="Cameroon" className="h-6 rounded-sm" /></h1>
            <p className="mt-1 text-lg text-slate-300" data-testid="athlete-position">{a.position}</p>
            <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              {age && <span><b className="text-slate-400">{t("Age:")}</b> {age}</span>}
              {stats.height && <span><b className="text-slate-400">{t("Height:")}</b> {stats.height}</span>}
              <span className="flex items-center gap-1"><Shirt size={14} className="text-slate-400" /> <b className="text-slate-400">{t("Current Club:")}</b> {a.club}</span>
              <span className="flex items-center gap-1"><MapPin size={14} className="text-slate-400" /> {a.club_country}</span>
            </div>
            {a.data_status === "missing" && <p className="mt-4 inline-block rounded bg-amber-500/20 px-3 py-1 text-xs font-bold text-amber-300" data-testid="data-pending-badge">Profile data pending — no verified online source found yet</p>}
          </div>
        </div>
        <div className="container-x relative flex gap-6 border-t border-white/10">
          {TABS.map((x) => <button key={x} onClick={() => setTab(x)} data-testid={`athlete-tab-${x.toLowerCase()}`} className={`border-b-[3px] py-3 text-sm font-bold ${tab === x ? "border-[#F5B301] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>{t(x)}</button>)}
        </div>
      </section>

      <div className="container-x grid gap-10 py-10 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          {tab === "Overview" && (
            <div>
              <h2 className="font-display text-2xl font-extrabold">{t("About")}</h2>
              <p className="mt-3 leading-relaxed text-slate-700" data-testid="athlete-bio">{a.bio}</p>
              <p className="mt-4 text-xs text-slate-400">Source: {a.source}{a.source_url && <> · <a href={a.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#047857] hover:underline" data-testid="athlete-source-link">Read on Wikipedia <ExternalLink size={11} /></a></>} · Fetched {fmtDate(a.fetched_at, false)} · Updated {fmtDate(a.updated_at, false)}</p>
              {a.news?.length > 0 && <div className="mt-10"><h2 className="font-display text-2xl font-extrabold">{t("In the News")}</h2><div className="mt-2">{a.news.slice(0, 4).map((n) => <NewsRow key={n.slug} item={n} />)}</div></div>}
              <PhotoGallery q={a.name} title="Photo Gallery" />
            </div>
          )}
          {tab === "News" && <div>{a.news?.length ? a.news.map((n) => <NewsRow key={n.slug} item={n} />) : <Empty text="No recent headlines mention this athlete yet." />}</div>}
          {tab === "Stats" && <StatsTable stats={stats} a={a} />}
          {tab === "Videos" && <div className="grid gap-4 sm:grid-cols-2">{a.videos?.length ? a.videos.map((v) => <VideoCard key={v.video_id} v={v} />) : <Empty text="No videos matched this athlete in our connected channels." />}</div>}
        </div>
        <aside className="space-y-6">
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-5">
            <h3 className="font-display text-xl font-extrabold">{t("Key Facts")}</h3>
            <dl className="mt-3 space-y-2 text-sm">
              {[["Nationality", t("Cameroon")], ["Sport", t(sportLabel(a.sport))], ["Position", a.position], ["Club / Team", a.club], ["Based in", a.club_country], ["Born", stats.born || "—"], ["Status", a.data_status === "verified" ? t("Verified online") : t("Data pending")]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-slate-200/60 pb-2 last:border-0"><dt className="text-slate-500">{t(k)}</dt><dd className="text-right font-semibold text-slate-900">{v}</dd></div>
              ))}
            </dl>
          </div>
          <div className="flex flex-wrap gap-2">{(a.tags || []).map((t) => <span key={t} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">#{t}</span>)}</div>
          <Link to={`/${a.sport}`} className="btn-outline w-full justify-center" data-testid="athlete-back-to-sport">{t("More")} {t(sportLabel(a.sport))}</Link>
        </aside>
      </div>
    </div>
  );
}

const StatsTable = ({ stats, a }) => {
  const rows = Object.entries(stats).filter(([, v]) => v);
  if (!rows.length) return <Empty text="Detailed statistics are not available from our connected free data sources for this athlete yet." />;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-100" data-testid="athlete-stats-table">
      <table className="w-full text-sm">
        <tbody>{rows.map(([k, v]) => <tr key={k} className="border-b border-slate-100 last:border-0"><td className="bg-slate-50 px-4 py-3 font-semibold capitalize text-slate-600">{k}</td><td className="px-4 py-3 text-slate-900">{v}</td></tr>)}</tbody>
      </table>
      <p className="bg-slate-50 px-4 py-2 text-xs text-slate-400">Source: TheSportsDB · {a.extra_sources?.[0]?.url}</p>
    </div>
  );
};
